import { service } from "@ember/service";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import DomainItem from "./domain-item";
import { task, enqueueTask, restartableTask, all, allSettled } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import destroyRecord from "glesys-controlpanel/utils/destroy-record";
import ENV from "glesys-controlpanel/config/environment";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class DnsRegisterController extends Controller {
  queryParams = ["query"];

  @service ajax;
  @service http;
  @service modal;
  @service router;
  @service store;

  get countries() {
    return this.model.countries.countries;
  }

  get pricelist() {
    return this.model.pricelist.pricelist;
  }

  get organization() {
    return this.model.organization;
  }

  get project() {
    return this.model.project;
  }

  @tracked query = "";
  @tracked searchedQuery = "";

  get isQueryEmpty() {
    return !this.query.length;
  }

  @tracked availableDomains = null;
  @tracked selectedDomains = null;

  get domainsAvailableForRegistration() {
    return this.availableDomains.filter((domain) => domain.status === "registrable");
  }

  get domainsAvailableForTransfer() {
    return this.availableDomains.filter((domain) => domain.status === "transferable");
  }

  get searchResults() {
    return [
      ...this.domainsAvailableForRegistration,
      ...this.domainsAvailableForTransfer,
      ...this.availableDomains.filter((domain) => domain.status === "unavailable"),
      ...this.availableDomains.filter((domain) => domain.status === "check-failed"),
      ...this.availableDomains.filter((domain) => domain.status === "unsupported-tld"),
    ];
  }

  get unsupportedTlds() {
    let tlds = [
      ...new Set(
        this.availableDomains.filter((domain) => domain.status === "unsupported-tld").map((domain) => domain.tld),
      ),
    ];

    return tlds.map((tld) => `.${tld}`).join(", ");
  }

  get domainsWithErrors() {
    let domains = this.selectedDomains;
    return domains.filter((domain) => {
      return domain.error != null;
    });
  }

  get searchableTlds() {
    let pricelist = this.pricelist;

    return Object.keys(pricelist).filter((tld) => {
      let prices = pricelist[tld];
      return prices.registration?.length && prices.renew?.length;
    });
  }

  constructor() {
    super(...arguments);

    this.availableDomains = [];
    this.selectedDomains = [];
  }

  searchTask = restartableTask(async () => {
    let search = this.query.replace(/\s/g, "").toLowerCase();

    this.searchedQuery = search;
    this.availableDomains = [];

    let domains = search
      .split(",")
      .filter(Boolean)
      .flatMap((domain) => (domain.includes(".") ? domain : this.searchableTlds.map((tld) => `${domain}.${tld}`)));

    this.appendDomains(this.mapUnsupportedDomains(domains.filter((domain) => !this.supportedTldFor(domain))));

    let chunks = this.chunkedDomainSearches(domains.filter((domain) => this.supportedTldFor(domain)));

    let results = await allSettled(chunks.map((chunk) => this.searchChunkTask.perform(chunk)));

    if (results.length && results.every((result) => result.state === "rejected")) {
      throw results[0].reason;
    }
  });

  searchChunkTask = enqueueTask({ maxConcurrency: 6 }, async (chunk) => {
    try {
      let data = await this.searchRequest(chunk.join(","));
      this.appendDomains(this.mapAvailableDomains(data.content.domain));
    } catch (error) {
      this.appendDomains(this.mapCheckFailedDomains(chunk));
      throw error;
    }
  });

  appendDomains(domains) {
    this.availableDomains = [...this.availableDomains, ...domains].toSorted((a, b) =>
      a.domainName.localeCompare(b.domainName),
    );
  }

  supportedTldFor(domainname) {
    return Object.keys(this.pricelist)
      .filter((tld) => domainname.endsWith(`.${tld}`))
      .toSorted((a, b) => b.length - a.length)
      .at(0);
  }

  mapUnsupportedDomains(domains) {
    return domains.map((domainname) => {
      let domain = new DomainItem(domainname, domainname.split(".").slice(1).join("."), false, [], undefined);
      domain.unsupportedTld = true;

      return domain;
    });
  }

  mapCheckFailedDomains(domains) {
    return domains.map((domainname) => {
      let tld = this.supportedTldFor(domainname);
      let domain = new DomainItem(domainname, tld, false, [], this.pricelist[tld]);
      domain.checkFailed = true;

      return domain;
    });
  }

  searchRequest(search) {
    return this.http.request({
      url: `${ENV.apiBaseUri}/domain/available`,
      method: "POST",
      body: {
        search,
        organizationnumber: this.project.organization.id,
      },
    });
  }

  mapAvailableDomains(domains) {
    let pricelist = this.pricelist;
    let selectedDomains = this.selectedDomains;

    return domains.map((item) => {
      let tld = this.supportedTldFor(item.domainname);
      let tldPrice = pricelist[tld];
      let domain = selectedDomains.find((domain) => domain.domainName === item.domainname);

      if (!domain) {
        domain = new DomainItem(item.domainname, tld, item.available === "yes", item.prices, tldPrice);
      }

      return domain;
    });
  }

  chunkedDomainSearches(domains, maxLength = 64) {
    domains = domains.filter((domain) => domain.length <= maxLength);

    let longest = Math.max(...domains.map((domain) => domain.length));
    let size = Math.max(1, Math.floor((maxLength + 1) / (longest + 1)));

    let chunks = [];
    for (let i = 0; i < domains.length; i += size) {
      chunks.push(domains.slice(i, i + size));
    }

    return chunks;
  }

  registerDomainTask = enqueueTask({ maxConcurrency: 3 }, async (domain, data) => {
    let isNew = false;
    let domainRecord = this.project
      .hasMany("dnsDomains")
      .value()
      .find((d) => {
        return d.id === domain.domainName || d.domainname === domain.domainName || d.displayname === domain.domainName;
      });

    if (!domainRecord) {
      isNew = true;
      domainRecord = this.store.createRecord("dns-domain", {
        project: this.project,
        domainname: domain.domainName,
      });

      try {
        await this.store.request(saveRecord(domainRecord));
      } catch (error) {
        domainRecord.unloadRecord();
        domain.error = error?.errors?.at(0)?.detail;
        return;
      }
    }

    data = {
      ...data,
      domainname: domainRecord.id,
      numyears: domain.period ? parseInt(domain.period, 10) : null,
      context: this.project.id,
    };

    try {
      await this.ajax.post(`${ENV.apiBaseUri}/domain/register`, {
        data,
      });
      domain.isDone = true;
    } catch (error) {
      domain.error = error?.content?.error?.message;
      if (isNew) {
        destroyRecord(domainRecord);
      }
    }
  });

  submitTask = task(async (data) => {
    let selectedDomains = this.selectedDomains.filter((domain) => !domain.isDone);
    let tasks = [];

    selectedDomains.map((domain) => {
      tasks.push(this.registerDomainTask.perform(domain, data));
    });

    await all(tasks);

    let hasErrors = !!this.selectedDomains.filter((domain) => !domain.isDone).length;

    if (!hasErrors) {
      this.router.transitionTo("dns");
    }
  });

  @action
  toggleSelected(domain) {
    domain.isSelected = !domain.isSelected;

    if (domain.isSelected) {
      this.selectedDomains = [...this.selectedDomains, domain];
    } else {
      this.selectedDomains = this.selectedDomains.filter((d) => d !== domain);
    }
  }

  @action
  onQueryChange(query) {
    this.query = query;
  }

  @action
  changePeriod(domain, event) {
    domain.period = parseInt(event.target.value);
  }
}
